import React, { useContext } from "react";
import { createStackNavigator } from "@react-navigation/stack";
import { Platform, StyleSheet } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Icon from "../components/Icon";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import ProfileScreen from "../screens/user/ProfileScreen";
import ScreenHeader from "../screens/user/ScreenHeader";
import InvoicesScreen from "../screens/client/InvoiceList";
import ColleaguesScreen from "../screens/client/ColleagueList";
import { useI18n } from "../hooks/i18n-hooks";
import UserLogsScreen from "../screens/user/UserLogs";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../hooks/use-current-profile";

export type ProfileStackParamList = {
    Profile: undefined;
    UserLogs: undefined;
    Invoices: undefined;
    Colleagues: undefined;
};

const Stack = createStackNavigator<ProfileStackParamList>();

const ProfileStack = () => {
    const cardStyleInterpolator = useCardStyleInterpolator();
    const i18n = useI18n();
    const theme = useTheme();
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();

    const isPrivateClient = currentProfile?.isPrivateClient || false;

    return (
        <Stack.Navigator
            screenOptions={{
                headerShown: false,
                cardStyleInterpolator,
                headerStatusBarHeight: 0,
                headerBackButtonDisplayMode: "minimal",
                headerBackImage: () => (
                    <Icon
                        name="back"
                        size={28}
                        style={{
                            marginLeft: Platform.OS === "ios" ? 10 : 0,
                            marginRight: 5,
                        }}
                    />
                ),
                headerBackTestID: "button-back",
                headerTitleAlign: "center",
                headerTitleStyle: {
                    ...theme.fonts.titleMedium,
                },
                gestureEnabled: true,
                headerShadowVisible: false,
            }}
            initialRouteName="Profile"
        >
            <Stack.Screen
                name="Profile"
                component={ProfileScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.client_profile.tabs.profile.title"),
                    header: () => (
                        <ScreenHeader
                            label={i18n.t(
                                `screens.profile_editor.title_${!isPrivateClient ? profileType : "private_client"}`,
                            )}
                        />
                    ),
                }}
            />
            <Stack.Screen
                name="UserLogs"
                component={UserLogsScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.activity_log.title"),
                }}
            />
            <Stack.Screen
                name="Invoices"
                component={InvoicesScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.my_invoices.title"),
                }}
            />
            <Stack.Screen
                name="Colleagues"
                component={ColleaguesScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("screens.user.my_colleagues.title"),
                }}
            />
        </Stack.Navigator>
    );
};

export default ProfileStack;
